import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../config.tsx";
import "../App.css";
import "../styles/main.css";
import styles from "../styles/allUsers.module.css";
import "bootstrap/dist/css/bootstrap.min.css";
import ChangeUserDataModal from "./modals/changeUserDataModal.tsx";
import ConfirmDeleteUserModal from "./modals/confirmDeleteUserModal.tsx";
import ConfirmHideModal from "./modals/confirmHideModal.tsx";
import ConfirmPermissionModal from "./modals/confirmPermissionModal.tsx";
import { User } from "./helpers/interfaces.tsx";
import { useTranslation } from "react-i18next";
import { correctUserRoleName } from "./helpers/helpers.tsx";
import apiFetch from "./helpers/requests";
import { useTableFilters } from "./hooks/useTableFilters.tsx";
import TableHeaderFilter from "./common/TableHeaderFilter.tsx";

interface SelectedTraining {
  id?: string;
  name?: string;
  entityId?: string;
  permission?: boolean;
  manufacturerId?: string;
  isHidden?: boolean;
}

const AllUsers = ({ userList, setUserList }) => {
  const { headquarterId, headsetId } = useParams();

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [trainingsList, setTrainingsList] = useState<any[]>([]);
  const [comingSoonManufacturers, setComingSoonManufacturers] = useState<any[]>(
    []
  );
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingTrainings, setIsLoadingTrainings] = useState(false);
  const [isUserVerified, setIsUserVerified] = useState(false);

  const [selectedUser, setSelectedUser] = useState<User>({});

  const [editingUsers, setEditingUsers] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [showConfirmDeleteUserModal, setShowConfirmDeleteUserModal] =
    useState(false);

  const [showChangeDataModal, setShowChangeDataModal] = useState(false);

  const [visibility, setVisibility] = useState({});
  const [showConfirmHideModal, setShowConfirmHideModal] = useState(false);
  const [showConfirmPermissionModal, setShowConfirmPermissionModal] =
    useState<SelectedTraining | false>(false);
  const [selectedManufacturerId, setSelectedManufacturerId] = useState("");
  const [selectedManufacturerName, setSelectedManufacturerName] = useState("");
  const [selectedTraining, setSelectedTraining] = useState<SelectedTraining>(
    {}
  );
  const [collapsedManufacturers, setCollapsedManufacturers] = useState<{
    [key: string]: boolean;
  }>({});
  const [pendingAction, setPendingAction] = useState<'selectAll' | 'hide'>('selectAll');
  const [pendingSelectAll, setPendingSelectAll] = useState<boolean>(true);

  const [streamingClientIds, setStreamingClientIds] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  // Filter hook
  const {
    filteredData: filteredUsers,
    filterPanels,
    filters,
    searchQueries,
    panelRefs,
    toggleFilterPanel,
    handleFilterChange,
    handleFilterSearch,
    setFilters
  } = useTableFilters(userList || []);

  const currentUserRole = (() => {
    try {
      const role = localStorage.getItem("userRole");
      return role ? JSON.parse(role) : null;
    } catch (e) {
      return localStorage.getItem("userRole");
    }
  })();

  const currentUserId = (() => {
    try {
      const uid = localStorage.getItem("userId");
      return uid ? JSON.parse(uid) : null;
    } catch (e) {
      return localStorage.getItem("userId");
    }
  })();

  const canEditUser = (targetUserRole) => {
    const roleHierarchy = {
      admin: 4,
      headquarterAdmin: 3,
      headsetAdmin: 2,
      user: 1,
      guest: 0,
    };

    const currentLevel = roleHierarchy[currentUserRole] || 0;
    const targetLevel = roleHierarchy[targetUserRole] || 0;

    if (currentUserRole === "admin") return true;

    return currentLevel > targetLevel;
  };

  const canEditSelectedUser = selectedUser.id
    ? canEditUser(selectedUser.role) || selectedUser.id === currentUserId
    : false;

  useEffect(() => {
    const ws = new WebSocket(`${API_BASE_URL}/ws/`);

    ws.onopen = () => {
      ws.send("getStreamingRooms");
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === "clientList") {
        setStreamingClientIds(Object.keys(message.clients));
      }
    };

    const fetchData = async () => {
      setIsLoadingUsers(true);
      await getUsersList();
      setIsLoadingUsers(false);
    };

    fetchData();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      ws.close();

      setUserList([]);
      setSelectedUser({});
      setEditingUsers({});
      setTrainingsList([]);
    };
  }, [setUserList]);

  useEffect(() => {
    updateUserListWithStreamingStatus();
  }, [isLoadingUsers, streamingClientIds]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible((prevVisible) => !prevVisible);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const getUsersList = async () => {
    let requestTarget = "";
    let requestBody = null;

    if (headquarterId && !headsetId) {
      requestTarget = "getUsersInHeadquarter";
      requestBody = JSON.stringify({ headquarterId });
    } else if (headsetId) {
      requestTarget = "getUsersInHeadset";
      requestBody = JSON.stringify({ headsetId });
    } else {
      requestTarget = "getUsersList";
    }

    try {
      const users = await apiFetch(`/${requestTarget}`, {
        method: "POST",
        body: requestBody,
      });
      setUserList(users);
    } catch (error) {
      console.error("Error get users list:", error);
      throw new Error("An error occurred while get users list");
    }
  };

  const updateUserListWithStreamingStatus = () => {
    setUserList((prevUserList) =>
      prevUserList.map((user) => ({
        ...user,
        isStreaming: streamingClientIds.includes(user.id),
      }))
    );
  };

  const checkUserVerification = async (email: string | null) => {
    try {
      const result = await apiFetch(`/checkUserVerification`, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      return result;
    } catch (error) {
      console.error("Error verify user:", error);
      throw new Error("An error occurred while checking verify user");
    }
  };

  const verifyUser = async (userId: string | null) => {
    try {
      setIsUserVerified(true);

      const userData = await apiFetch(`/verifyUser`, {
        method: "POST",
        body: JSON.stringify({ userId }),
      });
      return userData;
    } catch (error) {
      console.error("Error verify user:", error);
      throw new Error("An error occurred while verify user");
    }
  };

  const updatePermission = async (
    userId: string,
    trainingId: string,
    isChecked: boolean,
    isHidden: boolean = false
  ) => {
    try {
      await apiFetch(`/updateTrainingPermission`, {
        method: "POST",
        body: JSON.stringify({
          entityId: userId,
          entityType: "user",
          trainingId,
          isChecked,
          isHidden,
        }),
      });
    } catch (error) {
      console.error("Error update training permission:", error);
      throw new Error("An error occurred while update training permission");
    }
  };

  const fetchTrainingsList = async (userId: string) => {
    setIsLoadingTrainings(true);

    const showAll = !headquarterId && !headsetId;

    try {
      const trainings = await apiFetch(
        `/getTrainingsListWithPermissions`,
        {
          method: "POST",
          body: JSON.stringify({
            entityId: userId,
            entityType: "user",
            showAll: showAll,
          }),
        }
      );

      setTrainingsList(trainings);

      setVisibility((prevVisibility) => {
        const updatedVisibility = { ...prevVisibility };
        trainings.forEach((training) => {
          updatedVisibility[training.manufacturerId] =
            updatedVisibility[training.manufacturerId] || training.isHidden;
        });
        return updatedVisibility;
      });
    } catch (error) {
      console.error("Error get trainings list:", error);
      throw new Error("An error occurred while get trainings list");
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const fetchComingSoonManufacturers = async (userId: string) => {
    setIsLoadingTrainings(true);

    const showAll = !headquarterId && !headsetId;

    try {
      const manufacturers = await apiFetch(
        `/getComingSoonManufacturers`,
        {
          method: "POST",
          body: JSON.stringify({
            entityId: userId,
            entityType: "user",
            showAll,
          }),
        }
      );

      setComingSoonManufacturers(manufacturers);

      setVisibility((prevVisibility) => {
        const updatedVisibility = { ...prevVisibility };
        manufacturers.forEach((manufacturer) => {
          updatedVisibility[manufacturer.id] = manufacturer.isHidden;
        });
        return updatedVisibility;
      });
    } catch (error) {
      console.error("Error get coming soon manufacturers:", error);
      throw new Error("An error occurred while get coming soon manufacturers");
    } finally {
      setIsLoadingTrainings(false);
    }
  };

  const toggleDropdown = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };

  const handleDeleteUser = async () => {
    setUserList((prevUserList) =>
      prevUserList.filter((user) => user.id !== selectedUser.id)
    );
    setSelectedUser({});
    setEditingUsers({});
    setTrainingsList([]);

    setShowConfirmDeleteUserModal(false);
  };

  const handleChangeData = async (updatedUser: User) => {
    // Update the user list
    setUserList((prevUserList) =>
      prevUserList.map((user) =>
        user.id === updatedUser.id ? { ...user, ...updatedUser } : user
      )
    );

    // Update the selected user state
    setSelectedUser((prevSelectedUser) =>
      prevSelectedUser.id === updatedUser.id
        ? { ...prevSelectedUser, ...updatedUser }
        : prevSelectedUser
    );

    // Update permissions rendering
    handleEditClick(updatedUser);

    setShowChangeDataModal(false);
  };

  const handleEditClick = async (user: User) => {
    setEditingUsers((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((key) => {
        newState[key] = false;
      });
      newState[user.id] = true;
      return newState;
    });

    const verification = await checkUserVerification(user.email);
    setIsUserVerified(verification);

    setSelectedUser(user);

    await fetchTrainingsList(user.id);
    await fetchComingSoonManufacturers(user.id);
  };

  const handlePermissionChange = async (
    trainingId: string,
    targetId: string,
    newPermissionState: boolean
  ) => {
    const originalTrainingsList = trainingsList; // Store original for rollback
    setTrainingsList(
      trainingsList.map((training) => {
        if (training.id === trainingId) {
          return { ...training, permission: newPermissionState };
        }
        return training;
      })
    );

    try {
      await updatePermission(targetId, trainingId, newPermissionState);
    } catch (error) {
      console.error("Error updating training permission:", error);
      setTrainingsList(originalTrainingsList);
      alert(t("Failed to update permission. Please try again."));
    }
    setSelectedTraining({});
  };

  const toggleCollapse = (manufacturerId: string) => {
    setCollapsedManufacturers((prev) => ({
      ...prev,
      [manufacturerId]: !prev[manufacturerId],
    }));
  };

  const handleSelectAll = async (
    manufacturerId: string,
    shouldSelect: boolean
  ) => {
    setShowConfirmHideModal(false);
    const originalTrainingsList = [...trainingsList];
    
    // Optimistic update
    const updatedTrainings = trainingsList.map((training) => {
      if (training.manufacturerId === manufacturerId) {
        return { ...training, permission: shouldSelect };
      }
      return training;
    });
    setTrainingsList(updatedTrainings);

    // Identify changes needed
    const promises = originalTrainingsList
      .filter(
        (training) =>
          training.manufacturerId === manufacturerId &&
          training.permission !== shouldSelect
      )
      .map((training) =>
        updatePermission(
          selectedUser.id,
          training.id,
          shouldSelect,
          training.isHidden
        )
      );

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error("Error in bulk permission update:", error);
      setTrainingsList(originalTrainingsList); // Rollback on error
      alert(t("Failed to update permissions. Please try again."));
    }
  };

  const handleHideClick = (manufacturerId) => {
    setShowConfirmHideModal(false);
    const isHidden = visibility[manufacturerId];
    const shouldHide = !isHidden;

    if (
      comingSoonManufacturers.some(
        (comingSoonManufacturer) => comingSoonManufacturer.id === manufacturerId
      )
    ) {
      // Update permissions to false for manufacturer
      const updatedComingSoonManufacturers = comingSoonManufacturers.map(
        (comingSoonManufacturer) => {
          if (comingSoonManufacturer.id === manufacturerId) {
            updatePermission(
              selectedUser.id,
              comingSoonManufacturer.id,
              true,
              shouldHide
            );
            return { ...comingSoonManufacturer, isHidden: shouldHide };
          }
          return comingSoonManufacturers;
        }
      );

      setComingSoonManufacturers(updatedComingSoonManufacturers);
    } else {
      // Update permissions to false for all trainings under this manufacturer
      const updatedTrainings = trainingsList.map((training) => {
        if (training.manufacturerId === manufacturerId) {
          updatePermission(selectedUser.id, training.id, false, shouldHide);
          return { ...training, permission: false, isHidden: shouldHide };
        }
        return training;
      });

      setTrainingsList(updatedTrainings);
    }

    setVisibility((prevVisibility) => ({
      ...prevVisibility,
      [manufacturerId]: shouldHide,
    }));
  };

  const handleStreamClick = (userId) => {
    navigate(`/stream/${userId}`);
  };

  const renderConfirmDeleteUserModal = () => {
    return (
      <div className={"adminContainer"}>
        <ConfirmDeleteUserModal
          setShowConfirmDeleteUserModal={setShowConfirmDeleteUserModal}
          user={selectedUser}
          handleDeleteUser={handleDeleteUser}
        />
      </div>
    );
  };

  const renderChangeDataModal = () => {
    return (
      <div className={"adminContainer"}>
        <ChangeUserDataModal
          setShowChangeDataModal={setShowChangeDataModal}
          user={selectedUser}
          handleChangeData={handleChangeData}
          adminView={!headquarterId && !headsetId}
          headquarterView={!!headquarterId && !headsetId}
          groupView={!!headsetId}
        />
      </div>
    );
  };

  const renderConfirmHideModal = () => {
    let title = "";
    let description = "";
    let handleAction = (id) => {};

    if (pendingAction === 'selectAll') {
      title = pendingSelectAll ? t("Select_all") : t("Unselect_all");
      description = pendingSelectAll
        ? t("Confirm_to_add_the_permission")
        : t("Confirm_to_remove_the_permission");
      handleAction = (manufacturerId) => handleSelectAll(manufacturerId, pendingSelectAll);
    } else {
      const isHidden = visibility[selectedManufacturerId];
      title = isHidden ? t("Show_Manufacturer") : t("Hide_Manufacturer");
      description = isHidden 
        ? t("Confirm_to_show_this_manufacturer")
        : t("Confirm_to_hide_this_manufacturer");
      handleAction = handleHideClick;
    }

    return (
      <ConfirmHideModal
        setShowConfirmHideModal={setShowConfirmHideModal}
        manufacturerId={selectedManufacturerId}
        manufacturerName={selectedManufacturerName}
        usersView={true}
        handleHideClick={handleAction}
        title={title}
        description={description}
        confirmText={t("Confirm")}
        cancelText={t("Cancel")}
      />
    );
  };

  const handleToggleHide = (manufacturerId, manufacturerName) => {
    // Always show confirmation modal
    setSelectedManufacturerId(manufacturerId);
    setSelectedManufacturerName(manufacturerName);
    setPendingAction('hide');
    setShowConfirmHideModal(true);
  };

  const renderConfirmPermissionModal = () => {
    if (typeof showConfirmPermissionModal === 'boolean' && !showConfirmPermissionModal) return null; // If false, don't render

    const { id, name, entityId, permission } = showConfirmPermissionModal as SelectedTraining;

    return (
      <ConfirmPermissionModal
        setShowConfirmPermissionModal={() => setShowConfirmPermissionModal(false)}
        trainingId={id}
        trainingName={name}
        targetId={entityId}
        targetName={selectedUser.firstName + " " + selectedUser.lastName}
        permission={permission}
        handlePermissionChange={handlePermissionChange}
      />
    );
  };

  const groupTrainingsByManufacturer = (trainings) => {
    const groupedTrainings = {};
    trainings.forEach((training) => {
      const {
        id,
        name,
        manufacturerId,
        manufacturerName,
        permission,
        entityId,
        isHidden,
      } = training;
      if (!groupedTrainings[manufacturerId]) {
        groupedTrainings[manufacturerId] = {
          manufacturerName: manufacturerName,
          trainings: [],
        };
      }
      groupedTrainings[manufacturerId].trainings.push({
        id: id,
        name: name,
        permission: permission,
        entityId: entityId,
        isHidden: isHidden,
        manufacturerName: manufacturerName,
      });
    });

    return groupedTrainings;
  };

  const renderGroupedTrainings = (groupedTrainings: {
    [manufacturerId: string]: {
      manufacturerName: string;
      trainings: {
        id: string;
        entityId: string;
        name: string;
        permission: boolean;
        isHidden: boolean;
      }[];
    };
  }) => {
    return (
      <>
        {Object.entries(groupedTrainings).map(
          ([manufacturerId, { manufacturerName, trainings }]) => {
            const isCollapsed = collapsedManufacturers[manufacturerId];
            const allSelected = trainings.every((t) => t.permission);
            const isManufacturerHidden = trainings.every(
              (training) => training.isHidden
            );

            return (
              <div key={manufacturerId} className={styles.trainingsGroup}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {!isManufacturerHidden && ( // Only show collapse/expand if not hidden
                    <span
                      onClick={() => toggleCollapse(manufacturerId)}
                      style={{
                        cursor: "pointer",
                        marginRight: "10px",
                        fontSize: "0.8em",
                        userSelect: "none",
                      }}
                    >
                      {isCollapsed ? "\u25B6" : "\u25BC"}
                    </span>
                  )}
                  <label
                    className={styles.trainingsGroupTitle}
                    style={{ 
                      color: isManufacturerHidden ? "gray" : "#39a686",
                      cursor: "pointer",
                      marginBottom: 0 
                    }}
                    onClick={() => !isManufacturerHidden && toggleCollapse(manufacturerId)}
                  >
                    {manufacturerName}
                  </label>
                  {!isManufacturerHidden && !isCollapsed && canEditSelectedUser && (
                    <>
                      <span
                        onClick={() => {
                          setSelectedManufacturerId(manufacturerId);
                          setSelectedManufacturerName(manufacturerName);
                          setPendingSelectAll(!allSelected);
                          setPendingAction('selectAll');
                          setShowConfirmHideModal(true);
                        }}
                        style={{
                          marginLeft: "1vh",
                          marginTop: "0.4vh",
                          fontSize: "0.7em",
                          cursor: "pointer",
                          color: "#212529"
                        }}
                      >
                        {allSelected ? t("Unselect_all") : t("Select_all")}
                      </span>
                      {!headquarterId && !headsetId && (
                        <span
                          onClick={() => handleToggleHide(manufacturerId, manufacturerName)}
                          style={{
                            marginLeft: "1vh",
                            marginTop: "0.4vh",
                            fontSize: "0.7em",
                            cursor: "pointer",
                            color: "#212529"
                          }}
                        >
                          {isManufacturerHidden ? t("show") : t("hide")}
                        </span>
                      )}
                    </>
                  )}
                  {!headquarterId && !headsetId && isManufacturerHidden && canEditSelectedUser && (
                    <span
                      onClick={() => handleToggleHide(manufacturerId, manufacturerName)}
                      style={{
                        marginLeft: "1vh",
                        marginTop: "0.4vh",
                        fontSize: "0.7em",
                        cursor: "pointer",
                        color: "#212529"
                      }}
                    >
                      {t("show")}
                    </span>
                  )}
                </div>
                
                <div className={`${styles.collapsibleWrapper} ${isCollapsed || isManufacturerHidden ? styles.collapsed : ''}`}>
                  <div className={styles.collapsibleInner}>
                    <ul>
                      {trainings.map((training) => (
                        <li
                          key={training.id}
                          className="form-check custom-checkbox"
                          style={{ color: training.isHidden ? "gray" : "#212529" }}
                        >
                          <label className="custom-checkbox-label">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="agreeCheckbox"
                              checked={training.permission}
                              disabled={training.isHidden || !canEditSelectedUser}
                              onChange={(e) => {
                                setShowConfirmPermissionModal({
                                  id: training.id,
                                  name: training.name,
                                  entityId: training.entityId,
                                  permission: training.permission,
                                });
                              }}
                            />
                            <span className="checkmark"></span>
                            <span className="form-check-label">
                              {training.name}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          }
        )}
        {comingSoonManufacturers.length > 0 && (
          <label
            className={styles.trainingsGroupTitle}
            style={{ color: "#39a686", marginTop: "20px", display: "block" }}
          >
            {t("Coming_soon")}:
          </label>
        )}
        {comingSoonManufacturers.map(({ id, name, isHidden }) => {
          return (
            <div key={id} className={styles.trainingsGroup}>
              <label
                className={styles.trainingsGroupTitle}
                style={{ color: isHidden ? "gray" : "#212529" }}
              >
                {name}
              </label>
              {!headquarterId && !headsetId && canEditSelectedUser && (
                <span
                  onClick={() => handleToggleHide(id, name)}
                  style={{
                    marginLeft: "1vh",
                    fontSize: "0.7em",
                    cursor: "pointer",
                    color: "#212529"
                  }}
                >
                  {isHidden ? t("show") : t("hide")}
                </span>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const renderUserRole = (role) => {
    if (role === "user" || role === "guest") return "";

    return (
      <span
        style={{
          color: "#39a686",
          marginLeft: "1vh",
        }}
      >
        | {correctUserRoleName(role, t)}
      </span>
    );
  };

  const renderUserHeadsetCountry = (countryCode) => {
    return (
      <span
        style={{
          color: "#39a686",
          marginLeft: "1vh",
        }}
      >
        | {t("country_" + countryCode)}
      </span>
    );
  };

  return (
    <div className={styles.adminContainer}>
      {isLoadingUsers ? (
        <p className={styles.loading}>{t("Loading...")}</p>
      ) : (
        <>
          {/* Left side */}
          <div className={styles.leftContainer}>
            {/* Separate table header - don't find other way to avoid overlaping header on scroll */}
            <div className={styles.tableHeader}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: `calc(40%)` }}>
                      <TableHeaderFilter
                        column="firstName"
                        title={t("User")}
                        data={userList || []}
                        filterPanels={filterPanels}
                        filters={filters}
                        searchQueries={searchQueries}
                        panelRefs={panelRefs}
                        toggleFilterPanel={toggleFilterPanel}
                        handleFilterChange={handleFilterChange}
                        handleFilterSearch={handleFilterSearch}
                        setFilters={setFilters}
                      />
                    </th>
                    {!headquarterId && !headsetId && (
                      <th scope="col" style={{ width: `calc(20%)` }}>
                        <TableHeaderFilter
                          column="headquarterName"
                          title={t("Headquarter")}
                          data={userList || []}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                      </th>
                    )}
                    <th scope="col" style={{ width: `calc(20%)` }}>
                      <TableHeaderFilter
                          column="hospitalName"
                          title={t("Hospital")}
                          data={userList || []}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                    </th>
                    <th scope="col" style={{ width: `calc(20%)` }}></th>
                    <th scope="col" className={styles.editButtonWrapper}></th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* Table with restricted height and scrollbar */}
            <div className={styles.tableContent}>
              <div className={`container-fluid ${styles.containerFluid}`}>
                <table className="table">
                  <tbody>
                    {filteredUsers.map((user: User, index: number) => (
                      <tr
                        key={user.id}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#D4E5E2" : "transparent",
                        }}
                      >
                        <td style={{ width: `calc(40%)` }}>
                          <div className={styles.containerHeader}>
                            <img
                              src="/images/Icon_User_Simple.png"
                              style={{
                                width: "2.2vh",
                                marginLeft: "0.3vh",
                                marginRight: "1.5vh",
                              }}
                            />
                            <div className={styles.userDataWrapper}>
                              <div className={styles.userDataName}>
                                {user.firstName} {user.lastName}
                                {renderUserRole(user.role)}
                              </div>
                              <div className={styles.userDataEmail}>
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        {!headquarterId && !headsetId && (
                          <td style={{ width: `calc(20%)` }}>
                            {user.headquarterName}
                          </td>
                        )}
                        <td style={{ width: `calc(20%)` }}>
                          {user.hospitalId ? (
                            <>
                              {user.hospitalName}
                              {renderUserHeadsetCountry(user.headsetCountryCode)}
                            </>
                          ) : (
                            ""
                          )}
                        </td>
                        <td
                          style={{
                            color: "#39a686",
                            textAlign: "center",
                            width: `calc(20%)`,
                          }}
                        >
                          {user.isStreaming && (
                            <>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "20px",
                                  textAlign: "center",
                                }}
                              >
                                {isVisible
                                  ? String.fromCharCode(0x2b24)
                                  : "\u2003"}
                              </span>
                              <span>{t("Streaming")}</span>
                            </>
                          )}
                        </td>
                        <td className={styles.editButtonWrapper}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleEditClick(user)}
                            style={{
                              backgroundColor: editingUsers[user.id]
                                ? "#404040"
                                : "#39A686",
                              borderColor: editingUsers[user.id]
                                ? "#404040"
                                : "#39A686",
                            }}
                          >
                            {t("Details")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className={styles.rightContainer}>
            {selectedUser.id ? (
              <>
                <div className={styles.containerHeaderWrapper}>
                  <div className={styles.containerHeader}>
                    <img
                      className={styles.userDataImage}
                      src="/images/Icon_User_Simple.png"
                    />
                    <div className={styles.userDataWrapper}>
                      <div className={styles.userDataName}>
                        {selectedUser.firstName + " " + selectedUser.lastName}
                      </div>
                      <div className={styles.userDataEmail}>
                        {selectedUser.email}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {!isUserVerified && canEditSelectedUser && (
                      <>
                        <button
                          type="button"
                          className={`${styles.buttonVerify} btn btn-primary`}
                          onClick={() => verifyUser(selectedUser.id)}
                        >
                          {t("Verify")}
                        </button>
                      </>
                    )}
                    {selectedUser.isStreaming && (
                      <>
                        <button
                          type="button"
                          className={`${styles.buttonStream} btn btn-primary`}
                          onClick={() => handleStreamClick(selectedUser.id)}
                        >
                          {t("Stream")}
                        </button>
                      </>
                    )}
                    <div className="dropdown" ref={dropdownRef} style={{ visibility: canEditSelectedUser ? 'visible' : 'hidden' }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={toggleDropdown}
                        style={{
                          width: "13vh",
                          minWidth: "60px",
                          backgroundColor: "#39A686",
                          borderColor: "#39A686",
                          borderRadius: "10px",
                          fontSize: "1em",
                          fontFamily: "VirtualLabQuickSandLight",
                          fontWeight: "bold",
                          paddingRight: "30px",
                        }}
                      >
                        <span
                          style={{
                            marginRight: "1.8vh",
                          }}
                        >
                          &#9013;
                        </span>
                        {t("Options")}
                      </button>
                      {isOptionsOpen && (
                        <div className={styles.dropdownMenu}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsOptionsOpen(false);
                              setShowChangeDataModal(true);
                            }}
                            className="btn"
                          >
                            <img src="/images/Icon_Edit.png" />
                            {t("Edit_User")}
                          </button>

                          {selectedUser.id !== currentUserId && (
                            <button
                              type="button"
                              onClick={() => {
                                setIsOptionsOpen(false);
                                setShowConfirmDeleteUserModal(true);
                              }}
                              className="btn"
                            >
                              <img src="/images/Icon_Delete.png" />
                              {t("Delete_User")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <hr />

                  <p className={styles.containerHeaderTitle}>
                    {t("Training_permission")}
                  </p>
                  <p className={styles.containerHeaderDescription}>
                    {t("Set_user_access_to_these_trainings_in_the_VR_application")}
                  </p>
                </div>

                <div className={`${styles.trainingsContainer} thin-scrollbar`}>
                  {isLoadingTrainings ? (
                    <p>{t("Loading...")}</p>
                  ) : (
                    renderGroupedTrainings(
                      groupTrainingsByManufacturer(trainingsList)
                    )
                  )}
                </div>
              </>
            ) : (
              <p className={styles.selectUser}>{t("Select_user_to_edit")}</p>
            )}
          </div>
        </>
      )}
      {(showConfirmDeleteUserModal && renderConfirmDeleteUserModal()) ||
        (showChangeDataModal && renderChangeDataModal()) ||
        (showConfirmHideModal && renderConfirmHideModal()) ||
        (showConfirmPermissionModal && renderConfirmPermissionModal())}
    </div>
  );
};
export default AllUsers;